import { cx, cva, type VariantProps } from 'class-variance-authority'

const variants = cva('flex items-center justify-center', {
  variants: {
    variant: {
      round: 'rounded-full',
      square: 'rounded-sm',
    },
    size: {
      sm: 'size-5 text-xs',
      md: 'size-7 text-xs',
      lg: 'size-10 text-sm',
      xl: 'size-12 text-base',
    },
    type: {
      container: 'relative flex-none',
      content: 'absolute overflow-hidden size-full',
      label: 'text font-light leading-none text-white',
    },
  },
  defaultVariants: {
    variant: 'round',
    size: 'md',
  },
})

const badgeVariants = cva(
  'absolute rounded-full bg-orange-500 ring-white bottom-0 right-0',
  {
    variants: {
      size: {
        sm: 'size-1.5 ring-[1px]',
        md: 'size-2 ring-[1.5px]',
        lg: 'size-3 ring-[2px]',
        xl: 'size-3.5 ring-[2.5px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

const getInitials = (name: string | null | undefined) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map((word: string) => word.charAt(0))
    .slice(0, 3)
    .join('')
    .toUpperCase()
    .replace(/[^a-zA-Z0-9]/g, '') // remove special characters
}

const getTrueSize = (size: string) => {
  switch (size) {
    case 'sm':
      return 20
    case 'lg':
      return 40
    case 'xl':
      return 48
    case 'md':
    default:
      return 28
  }
}

type AvatarProps = {
  name: string | null | undefined
  color?: string
  picture?: string | undefined
  notification?: boolean
  variant?: 'round' | 'square'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
} & VariantProps<typeof variants>

export const Avatar = ({
  name,
  color,
  picture,
  notification = false,
  variant,
  size,
  className,
}: AvatarProps) => {
  const hasPicture = !!picture && picture !== '/img/logo-skop-white.png'
  const bgColor = hasPicture ? '#f5f5f5' : color || '#71717A'
  const initials = getInitials(name)
  const trueSize = getTrueSize(size)

  return (
    <div
      className={cx(variants({ variant, size, type: 'container', className }))}
      style={{ backgroundColor: bgColor }}
    >
      <div className={cx(variants({ variant, type: 'content' }))}>
        {hasPicture ? (
          <img
            src={picture}
            alt={name}
            width={trueSize}
            height={trueSize}
            className='object-contain size-full'
          />
        ) : (
          <span className={cx(variants({ size, type: 'label' }))}>
            {initials}
          </span>
        )}
      </div>
      {notification && <span className={cx(badgeVariants({ size }))} />}
    </div>
  )
}
